import AIAutofillResources, { type GenerateCustomFieldValueParams } from 'Resources/AIAutofill';
import useComponentDidUnmount from 'App/components/hooks/useComponentDidUnmount';
import { Block, ClickableItem, Loader, Tooltip } from '@upsales/components';
import type { AIAutofillDataType } from 'App/resources/Model/AIAutofill';
import { type CancelablePromise, makeCancelable } from 'Helpers/promise';
import { useTranslation } from 'Components/Helpers/translate';
import { useSoftDeployAccess } from 'App/components/hooks';
import BemClass from '@upsales/components/Utils/bemClass';
import ClientResource from 'App/resources/Client';
import React, { useState, useRef } from 'react';
import logError from 'Helpers/logError';
import './GenerateAI.scss';

type Props = {
	onClick: (data: AIAutofillDataType[]) => void;
	space?: string;
	disabled?: boolean;
	parameters: GenerateCustomFieldValueParams;
};

const GenerateAI = ({ onClick, space, disabled = false, parameters }: Props) => {
	const [loading, setLoading] = useState(false);
	const { t } = useTranslation();
	const hasAICustomValue = useSoftDeployAccess('AI_CUSTOM_VALUE');
	const aiAutoFillReq = useRef<CancelablePromise<Awaited<ReturnType<typeof ClientResource.get>>> | null>(null);

	useComponentDidUnmount(() => {
		aiAutoFillReq.current?.cancel?.();
	});

	if (!hasAICustomValue) {
		return null;
	}

	const classes = new BemClass('GenerateAI');

	const onAIGenerateClick = async () => {
		setLoading(true);

		aiAutoFillReq.current = makeCancelable(AIAutofillResources.generateCustomFieldValue(parameters));
		aiAutoFillReq.current.promise
			.then(({ data }) => onClick(data))
			.catch(error => logError(error))
			.finally(() => setLoading(false));
	};

	return (
		<Block className={classes.b()} space={space}>
			{loading ? (
				<Loader size="xs" />
			) : (
				<Tooltip title={t('account.customField.generateAI')}>
					<ClickableItem icon="stars" size="sm" onClick={onAIGenerateClick} disabled={disabled} />
				</Tooltip>
			)}
		</Block>
	);
};

export default GenerateAI;
